<div class="e-card view-card">
  <!-- Header -->
  <div class="detail-header">
    <div class="detail-header-left">
      <button type="button" class="e-btn e-flat" (click)="goBack()">
        <span class="ti ti-arrow-left icon-md"></span>
      </button>
      <div class="breadcrumb">
        <a routerLink="/notifications" class="breadcrumb-link">{{
          "notifications" | translate
        }}</a>
        <span class="breadcrumb-separator">›</span>
        <a routerLink="/notifications/email-queue" class="breadcrumb-link">{{
          "emailQueue" | translate
        }}</a>
        <span class="breadcrumb-separator">›</span>
        <span class="breadcrumb-current">{{ "emailDetail" | translate }}</span>
      </div>
    </div>
  </div>

  @if (isLoading()) {
    <div class="loading-state">
      <span class="ti ti-loader-2 spinning icon-xl"></span>
      <span>{{ "loading" | translate }}</span>
    </div>
  }

  @if (!isLoading() && email()) {
    <div class="detail-body">
      <!-- Status Banner -->
      <div
        class="status-banner"
        [style.background-color]="emailStatus()?.bgColor || '#f0f0f0'"
        [style.color]="emailStatus()?.fgColor || '#333'"
      >
        <span class="banner-subject">{{
          emailStatus()?.name || email()!.status
        }}</span>
      </div>

      <!-- Info Grid -->
      <div class="info-grid">
        <div class="info-section">
          <h4 class="section-title">{{ "emailInfo" | translate }}</h4>

          <div class="info-row">
            <span class="info-label">{{ "id" | translate }}</span>
            <span class="info-value mono">{{ email()!.id }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ "toEmail" | translate }}</span>
            <span class="info-value">{{ email()!.toEmail }}</span>
          </div>
          @if (email()!.fromEmail) {
            <div class="info-row">
              <span class="info-label">{{ "fromEmail" | translate }}</span>
              <span class="info-value">{{ email()!.fromEmail }}</span>
            </div>
          }
          @if (email()!.ccEmail?.length) {
            <div class="info-row">
              <span class="info-label">{{ "ccEmail" | translate }}</span>
              <span class="info-value">{{ email()!.ccEmail!.join(", ") }}</span>
            </div>
          }
          @if (email()!.bccEmail?.length) {
            <div class="info-row">
              <span class="info-label">{{ "bccEmail" | translate }}</span>
              <span class="info-value">{{
                email()!.bccEmail!.join(", ")
              }}</span>
            </div>
          }
          <div class="info-row">
            <span class="info-label">{{ "subject" | translate }}</span>
            <span class="info-value">{{ email()!.subject }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ "retryCount" | translate }}</span>
            <span class="info-value">{{ email()!.retryCount }}</span>
          </div>
          @if (email()!.tenantId) {
            <div class="info-row">
              <span class="info-label">{{ "tenantId" | translate }}</span>
              <span class="info-value mono">{{ email()!.tenantId }}</span>
            </div>
          }
        </div>

        <div class="info-section">
          <h4 class="section-title">{{ "timeline" | translate }}</h4>

          <div class="info-row">
            <span class="info-label">{{ "createdAt" | translate }}</span>
            <span class="info-value">{{
              email()!.createdAt | date: "medium"
            }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ "sendItOn" | translate }}</span>
            <span class="info-value">{{
              email()!.sendItOn | date: "medium"
            }}</span>
          </div>
          @if (email()!.sentAt) {
            <div class="info-row">
              <span class="info-label">{{ "sentAt" | translate }}</span>
              <span class="info-value success-text">{{
                email()!.sentAt | date: "medium"
              }}</span>
            </div>
          }
          @if (email()!.updatedAt) {
            <div class="info-row">
              <span class="info-label">{{ "updatedAt" | translate }}</span>
              <span class="info-value">{{
                email()!.updatedAt | date: "medium"
              }}</span>
            </div>
          }
        </div>
      </div>

      <!-- Failure reason -->
      @if (email()!.failureReason) {
        <div class="code-section">
          <h4 class="section-title error-title">
            <span class="ti ti-alert-circle"></span>
            {{ "failureReason" | translate }}
          </h4>
          <div class="error-message">{{ email()!.failureReason }}</div>
        </div>
      }

      <!-- Attachments -->
      @if (email()!.attachments?.length) {
        <div class="code-section">
          <h4 class="section-title">
            <span class="ti ti-paperclip"></span>
            {{ "attachments" | translate }}
          </h4>
          <div class="attachment-list">
            @for (att of email()!.attachments; track att.s3Path) {
              <div class="attachment-row">
                <span class="attachment-name">{{ att.filename }}</span>
                <span class="attachment-meta"
                  >{{ att.contentType }} · {{ formatSize(att.size) }}</span
                >
              </div>
            }
          </div>
        </div>
      }

      <!-- Body -->
      @if (email()!.bodyHtml) {
        <div class="code-section">
          <h4 class="section-title">
            <span class="ti ti-mail-opened"></span>
            {{ "bodyHtml" | translate }}
          </h4>
          <div class="html-preview" [innerHTML]="email()!.bodyHtml"></div>
        </div>
      }
      @if (email()!.bodyText) {
        <div class="code-section">
          <h4 class="section-title">
            <span class="ti ti-align-left"></span>
            {{ "bodyText" | translate }}
          </h4>
          <pre class="code-block">{{ email()!.bodyText }}</pre>
        </div>
      }

      <!-- Recipient -->
      @if (email()!.recipient && (email()!.recipient | json) !== "{}") {
        <div class="code-section">
          <h4 class="section-title">
            <span class="ti ti-user"></span>
            {{ "recipient" | translate }}
          </h4>
          <pre class="code-block">{{ formatJson(email()!.recipient) }}</pre>
        </div>
      }

      <!-- Metadata -->
      @if (email()!.metadata && (email()!.metadata | json) !== "{}") {
        <div class="code-section">
          <h4 class="section-title">
            <span class="ti ti-braces"></span>
            {{ "metadata" | translate }}
          </h4>
          <pre class="code-block">{{ formatJson(email()!.metadata) }}</pre>
        </div>
      }
    </div>
  }

  @if (!isLoading() && !email()) {
    <div class="empty-state">
      <span class="ti ti-mood-sad icon-xl"></span>
      <p>{{ "emailNotFound" | translate }}</p>
      <button type="button" class="e-btn e-primary" (click)="goBack()">
        {{ "backToList" | translate }}
      </button>
    </div>
  }
</div>
